import React from 'react'
import { useForm, Link, usePage } from '@inertiajs/react'
import Layout from '../../components/Layout'
import Breadcrumb from '../../components/Breadcrumb'
import QuadraMapLibre from '../../components/maps/QuadraMapLibre'
import FormField from '../../components/forms/FormField'
import BooleanField from '../../components/forms/BooleanField'
import RadioGroupField from '../../components/forms/RadioGroupField'
import SelectField from '../../components/forms/SelectField'
import DateSelectField from '../../components/forms/DateSelectField'
import ErrorSummary from '../../components/forms/ErrorSummary'

function InfoItem({ label, value }) {
  let displayValue = value
  if (typeof value === 'boolean') {
    displayValue = value ? 'Sim' : 'Não'
  } else if (value === null || value === undefined || value === '') {
    displayValue = '-'
  }

  return (
    <div className="flex gap-2 py-1">
      <span className="font-medium text-gray-700">{label}:</span>
      <span className="text-gray-600">{displayValue}</span>
    </div>
  )
}

export default function QuadraShow({
  quadra,
  polo,
  surroundingPolos = [],
  lotesInPolo = [],
  lotesOutside = [],
  assigned = [],
  canEdit = false,
  canRevise = false,
  isAdmin = false
}) {
  const { errors } = usePage().props
  const task = canEdit ? 'input_data' : canRevise ? 'revise' : null

  const { data, setData, patch, processing } = useForm({
    task: task,
    quadra: {
      // Text/date fields
      data_levantamento: quadra.data_levantamento || '',
      polo_id: quadra.polo_id || '',
      // Boolean fields
      cabeamento_enterrado: quadra.cabeamento_enterrado,
      faixa_livre_pedestres: quadra.faixa_livre_pedestres,
      semaforo_pedestres: quadra.semaforo_pedestres,
      semaforo_veiculos: quadra.semaforo_veiculos,
      faixa_pedestres: quadra.faixa_pedestres,
      obras_publicas: quadra.obras_publicas,
      piso_tatil: quadra.piso_tatil,
      piso_rebaixado: quadra.piso_rebaixado,
      mao_dupla: quadra.mao_dupla,
      // Number fields
      densidade_demografica_min: quadra.densidade_demografica_min ?? '',
      densidade_demografica_max: quadra.densidade_demografica_max ?? '',
      // Enum fields - use null, NOT empty string (PostgreSQL enums reject "")
      largura_calcada: quadra.largura_calcada || null,
      velocidade_veiculos: quadra.velocidade_veiculos || null,
      bicicleta: quadra.bicicleta || null,
      limpeza_rua: quadra.limpeza_rua || null,
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    patch(`/quadras/${quadra.id}`, { forceFormData: true })
  }

  const quadraErrors = errors?.quadra || errors || {}

  const breadcrumbItems = polo
    ? [{ label: 'Polo ' + polo.nome, href: `/polos/${polo.id}` }]
    : []

  return (
    <Layout>
      <div className="py-8">
        {breadcrumbItems.length > 0 && <Breadcrumb items={breadcrumbItems} />}

        <h1 className="text-2xl font-bold text-brand-dark mb-4 font-heading">Quadra</h1>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Info Section */}
          <div className="card p-4">
            <h2 className="text-lg font-semibold text-brand-dark mb-4">Informações Atuais</h2>
            {task ? (
              <form onSubmit={handleSubmit}>
                <ErrorSummary errors={errors?.quadra || errors} />
                <div className="space-y-1 mb-4">
                  <InfoItem label="ID" value={quadra.id} />
                  <InfoItem label="Polo" value={polo?.nome} />
                </div>

                <DateSelectField
                  label="Data do levantamento"
                  name="quadra[data_levantamento]"
                  value={data.quadra.data_levantamento}
                  onChange={(e) => setData('quadra', { ...data.quadra, data_levantamento: e.target.value })}
                  error={quadraErrors.data_levantamento}
                />

                {surroundingPolos.length > 0 && (
                  <SelectField
                    label="A qual polo essa quadra pertence?"
                    name="quadra[polo_id]"
                    options={surroundingPolos.map(p => ({ label: p.nome, value: p.id }))}
                    value={data.quadra.polo_id}
                    onChange={(e) => setData('quadra', { ...data.quadra, polo_id: e.target.value })}
                    placeholder="Nenhum"
                    error={quadraErrors.polo_id}
                  />
                )}

                <BooleanField
                  label="O cabeamento é enterrado?"
                  name="quadra[cabeamento_enterrado]"
                  value={data.quadra.cabeamento_enterrado}
                  onChange={(val) => setData('quadra', { ...data.quadra, cabeamento_enterrado: val })}
                  error={quadraErrors.cabeamento_enterrado}
                />

                <BooleanField
                  label="Há uma faixa de 1,20m livre para pedestres na calçada?"
                  name="quadra[faixa_livre_pedestres]"
                  value={data.quadra.faixa_livre_pedestres}
                  onChange={(val) => setData('quadra', { ...data.quadra, faixa_livre_pedestres: val })}
                  error={quadraErrors.faixa_livre_pedestres}
                />

                <BooleanField
                  label="Há semáforos para pedestres?"
                  name="quadra[semaforo_pedestres]"
                  value={data.quadra.semaforo_pedestres}
                  onChange={(val) => setData('quadra', { ...data.quadra, semaforo_pedestres: val })}
                  error={quadraErrors.semaforo_pedestres}
                />

                <BooleanField
                  label="Há semáforos para veículos?"
                  name="quadra[semaforo_veiculos]"
                  value={data.quadra.semaforo_veiculos}
                  onChange={(val) => setData('quadra', { ...data.quadra, semaforo_veiculos: val })}
                  error={quadraErrors.semaforo_veiculos}
                />

                <BooleanField
                  label="Há faixas de pedestres sem semáforos?"
                  name="quadra[faixa_pedestres]"
                  value={data.quadra.faixa_pedestres}
                  onChange={(val) => setData('quadra', { ...data.quadra, faixa_pedestres: val })}
                  error={quadraErrors.faixa_pedestres}
                />

                <BooleanField
                  label="Há obras públicas?"
                  name="quadra[obras_publicas]"
                  value={data.quadra.obras_publicas}
                  onChange={(val) => setData('quadra', { ...data.quadra, obras_publicas: val })}
                  error={quadraErrors.obras_publicas}
                />

                <BooleanField
                  label="Há piso tátil para PNE?"
                  name="quadra[piso_tatil]"
                  value={data.quadra.piso_tatil}
                  onChange={(val) => setData('quadra', { ...data.quadra, piso_tatil: val })}
                  error={quadraErrors.piso_tatil}
                />

                <BooleanField
                  label="Há piso rebaixado para PNE?"
                  name="quadra[piso_rebaixado]"
                  value={data.quadra.piso_rebaixado}
                  onChange={(val) => setData('quadra', { ...data.quadra, piso_rebaixado: val })}
                  error={quadraErrors.piso_rebaixado}
                />

                <RadioGroupField
                  label="Largura da calçada, em metros"
                  name="quadra[largura_calcada]"
                  options={{ 'Até 2m': '0-2m', '2 a 4m': '2-4m', 'Acima de 4m': '4+' }}
                  value={data.quadra.largura_calcada}
                  onChange={(val) => setData('quadra', { ...data.quadra, largura_calcada: val })}
                  error={quadraErrors.largura_calcada}
                />

                <RadioGroupField
                  label="Velocidade máxima para veículos"
                  name="quadra[velocidade_veiculos]"
                  options={{ 'Até 40km/h': '0-40km/h', 'Entre 40 e 60km/h': '40-60km/h', 'Acima de 60km/h': '>60km/h' }}
                  value={data.quadra.velocidade_veiculos}
                  onChange={(val) => setData('quadra', { ...data.quadra, velocidade_veiculos: val })}
                  error={quadraErrors.velocidade_veiculos}
                />

                <RadioGroupField
                  label="Há ciclovia ou ciclofaixa?"
                  name="quadra[bicicleta]"
                  options={{ 'Não': 'nao', 'Ciclovia': 'ciclovia', 'Ciclofaixa': 'ciclofaixa' }}
                  value={data.quadra.bicicleta}
                  onChange={(val) => setData('quadra', { ...data.quadra, bicicleta: val })}
                  error={quadraErrors.bicicleta}
                />

                <RadioGroupField
                  label="Qualidade da limpeza da rua"
                  name="quadra[limpeza_rua]"
                  options={{ 'Ótima': 'otima', 'Boa': 'boa', 'Ruim': 'ruim' }}
                  value={data.quadra.limpeza_rua}
                  onChange={(val) => setData('quadra', { ...data.quadra, limpeza_rua: val })}
                  error={quadraErrors.limpeza_rua}
                />

                <BooleanField
                  label="É uma rua de mão dupla?"
                  name="quadra[mao_dupla]"
                  value={data.quadra.mao_dupla}
                  onChange={(val) => setData('quadra', { ...data.quadra, mao_dupla: val })}
                  error={quadraErrors.mao_dupla}
                />

                <FormField
                  label="Densidade demográfica mínima (disponível no GeoSampa)"
                  name="quadra[densidade_demografica_min]"
                  type="number"
                  value={data.quadra.densidade_demografica_min}
                  onChange={(e) => setData('quadra', { ...data.quadra, densidade_demografica_min: e.target.value })}
                  error={quadraErrors.densidade_demografica_min}
                />

                <FormField
                  label="Densidade demográfica máxima (disponível no GeoSampa)"
                  name="quadra[densidade_demografica_max]"
                  type="number"
                  value={data.quadra.densidade_demografica_max}
                  onChange={(e) => setData('quadra', { ...data.quadra, densidade_demografica_max: e.target.value })}
                  error={quadraErrors.densidade_demografica_max}
                />

                <button
                  type="submit"
                  disabled={processing}
                  className="mt-2 px-6 py-2 bg-brand-dark hover:bg-brand-light text-white font-medium rounded-md transition-colors disabled:opacity-50"
                >
                  {processing ? 'Atualizando...' : 'Atualizar'}
                </button>
              </form>
            ) : (
              <div className="space-y-1">
                <InfoItem label="ID" value={quadra.id} />
                <InfoItem label="Polo" value={polo?.nome} />
                <InfoItem label="Data do levantamento" value={quadra.data_levantamento} />
                <InfoItem label="Cabeamento enterrado" value={quadra.cabeamento_enterrado} />
                <InfoItem label="Faixa livre pedestres" value={quadra.faixa_livre_pedestres} />
                <InfoItem label="Semáforo pedestres" value={quadra.semaforo_pedestres} />
                <InfoItem label="Semáforo veículos" value={quadra.semaforo_veiculos} />
                <InfoItem label="Faixa pedestres" value={quadra.faixa_pedestres} />
                <InfoItem label="Obras públicas" value={quadra.obras_publicas} />
                <InfoItem label="Piso tátil" value={quadra.piso_tatil} />
                <InfoItem label="Piso rebaixado" value={quadra.piso_rebaixado} />
                <InfoItem label="Largura calçada" value={quadra.largura_calcada} />
                <InfoItem label="Velocidade veículos" value={quadra.velocidade_veiculos} />
                <InfoItem label="Bicicleta" value={quadra.bicicleta} />
                <InfoItem label="Limpeza rua" value={quadra.limpeza_rua} />
                <InfoItem label="Mão dupla" value={quadra.mao_dupla} />
                <InfoItem label="Densidade demográfica min" value={quadra.densidade_demografica_min} />
                <InfoItem label="Densidade demográfica max" value={quadra.densidade_demografica_max} />
              </div>
            )}
          </div>

          {/* Map Section */}
          <div className="card p-4">
            <h2 className="text-lg font-semibold text-brand-dark mb-4">Mapa</h2>
            <QuadraMapLibre quadraId={quadra.id} className="h-[400px]" />
          </div>
        </div>

        {/* Lotes Lists */}
        {(lotesInPolo.length > 0 || lotesOutside.length > 0) && (
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Lotes in Polo */}
            <div className="card p-4">
              <h3 className="font-semibold text-brand-dark mb-2">Lotes no Polo</h3>
              {lotesInPolo.length === 0 ? (
                <p className="text-gray-500 italic">Nenhum lote no polo</p>
              ) : (
                <ul className="space-y-1">
                  {lotesInPolo.map(lote => (
                    <li key={lote.id}>
                      <Link href={`/lotes/${lote.id}`} className="link">
                        Lote {lote.id}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Lotes outside Polo */}
            <div className="card p-4">
              <h3 className="font-semibold text-brand-dark mb-2">Outros Lotes</h3>
              {lotesOutside.length === 0 ? (
                <p className="text-gray-500 italic">Nenhum outro lote</p>
              ) : (
                <ul className="space-y-1">
                  {lotesOutside.map(lote => (
                    <li key={lote.id}>
                      <Link href={`/lotes/${lote.id}`} className="link-subtle">
                        Lote {lote.id}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Responsibles Section - Visible to all */}
        <div className="card p-6">
          <h2 className="text-xl font-bold text-brand-dark mb-2">Responsáveis pela quadra:</h2>
          {isAdmin && (
            <p className="text-gray-500 text-sm italic mb-4">(Altere-os na página do polo)</p>
          )}
          {assigned.length === 0 ? (
            <p className="text-gray-500 italic ml-4">Ainda não há responsáveis pela quadra</p>
          ) : (
            <ul className="ml-4 space-y-1">
              {assigned.map(user => (
                <li key={user.id} className="font-medium">{user.full_name}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Layout>
  )
}
